import { useState, type FormEvent, useEffect } from "react";
import { Link, Navigate, useNavigate } from "react-router";
import { Alert, Button, TextInput, useToast } from "@/ui";
import { AuthCard, AuthLayout } from "@/layout/AuthLayout";
import { useAuth } from "@/auth/AuthProvider";
import { ApiClientError } from "@/lib/api";

/** Step 2 of platform sign-in — enter the OTP sent to email. */
export function PlatformTwoFa() {
  const { user, twoFaPending, verifyTwoFa, resendTwoFa, logout } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();

  const [code, setCode] = useState("");
  const [fields, setFields] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [resending, setResending] = useState(false);
  const [devCode, setDevCode] = useState<string | null>(null);

  useEffect(() => {
    const stored = sessionStorage.getItem("twoFaDevCode");
    if (stored) {
      setDevCode(stored);
      sessionStorage.removeItem("twoFaDevCode");
    }
  }, []);

  // After successful 2FA, both user and twoFaPending update — navigate home
  useEffect(() => {
    if (!twoFaPending && user) {
      navigate(homeFor(user), { replace: true });
    }
  }, [twoFaPending, user, navigate]);

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setFields({});
    setSubmitting(true);
    try {
      await verifyTwoFa(code);
      // Navigation handled by useEffect once context updates propagate
    } catch (err) {
      if (err instanceof ApiClientError) {
        setFields(err.fields ?? {});
        toast.error(err.message);
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function handleResend(): Promise<void> {
    setResending(true);
    try {
      const result = await resendTwoFa();
      if (result.devCode) {
        setDevCode(result.devCode);
        sessionStorage.removeItem("twoFaDevCode");
      }
      toast.success("A new code has been sent.");
    } catch {
      toast.error("Could not resend. Please try again.");
    } finally {
      setResending(false);
    }
  }

  return (
    <AuthLayout
      accent="almost there."
      blurb="We sent a 6-digit verification code to the email on file."
      headline="Verify your identity,"
      points={["The code expires in 5 minutes", "Check your spam folder if you don't see it"]}
    >
      <AuthCard subtitle="Two-factor authentication" title="Enter verification code">
        {devCode ? (
          <Alert kind="info">Dev code: {devCode}</Alert>
        ) : null}
        <form className="flex flex-col gap-4" noValidate onSubmit={handleSubmit}>
          <TextInput
            autoFocus
            error={fields.code}
            label="Verification code"
            maxLength={6}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            placeholder="000000"
            required
            value={code}
          />
          <Button block loading={submitting} type="submit">
            Verify
          </Button>
          <Button variant="secondary" loading={resending} onClick={handleResend} type="button">
            Resend code
          </Button>
          <Link
            className="text-center text-sm font-medium text-brand-ink underline"
            to="/login"
            onClick={() => {
              void logout();
            }}
          >
            Cancel
          </Link>
        </form>
      </AuthCard>
    </AuthLayout>
  );
}

function homeFor(user: { role: string }): string {
  if (user.role === "super_admin") return "/platform";
  return "/";
}
